import { API_URL } from "utils/constants";

const BASE_PATH = "/booking/print-layout-settings";

const request = async (path, options = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || "Print layout API error");
    error.status = response.status;
    error.code = payload.code;
    error.data = payload.data;
    error.errors = payload.errors;
    throw error;
  }
  return payload;
};

export const resolvePrintLayout = async (printType, scope = {}) => {
  const params = new URLSearchParams({ print_type: printType });
  Object.entries(scope).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") params.set(key, value);
  });
  try {
    const response = await request(`${BASE_PATH}/resolve?${params.toString()}`);
    return response.data || null;
  } catch (error) {
    if (error.status === 404) return null;
    throw error;
  }
};

export const savePrintLayoutRemote = (layoutId, payload) =>
  request(layoutId ? `${BASE_PATH}/${layoutId}` : BASE_PATH, {
    method: layoutId ? "PUT" : "POST",
    body: JSON.stringify(payload),
  });

export const uploadPrintLayoutLogo = (layoutId, file) => {
  const body = new FormData();
  body.append("logo", file);
  return request(`${BASE_PATH}/${layoutId}/logo`, { method: "POST", body });
};

export const printLayoutToEditorState = (layout) => {
  if (!layout) return null;
  return {
    ...layout,
    paperWidth: layout.paper_width,
    printerFont: layout.printer_font,
    charsPerLine: layout.characters_per_line,
    ...(Object.prototype.hasOwnProperty.call(layout, "character_spacing")
      ? { characterSpacing: layout.character_spacing }
      : {}),
    lineHeight: layout.line_height,
    companyName: layout.header?.company_name?.value,
    companyAddress: layout.header?.company_address?.value,
    footerText: layout.footer?.text || "",
    logoDataUrl: layout.header?.logo_url || "",
    fields: (layout.booking_details || []).map((field) => ({
      ...field,
      id: field.id || field.key,
      fontSize: field.size === "large" ? 16 : 12,
    })),
    itemStyles: [
      { id: "itemHeader", label: "Activity Header", sample: "Activity Qty", visible: layout.activity?.visible !== false, bold: true, align: "left", fontSize: 11 },
      { id: "itemName", label: "Activity Name", sample: "ATV Single", visible: layout.activity?.item_visible !== false, bold: layout.activity?.item_bold !== false, align: layout.activity?.item_align || "left", fontSize: 14 },
      { id: "itemQty", label: "Qty", sample: "2", visible: layout.activity?.quantity_visible !== false, bold: layout.activity?.quantity_bold !== false, align: layout.activity?.quantity_align || "right", fontSize: 14 },
      { id: "mealName", label: "Meal Item", sample: "Lunch Package", visible: layout.meal?.visible !== false, bold: layout.meal?.item_bold === true, align: "left", fontSize: 14 },
      { id: "mealQty", label: "Meal Qty", sample: "2", visible: layout.meal?.visible !== false, bold: layout.meal?.quantity_bold === true, align: "right", fontSize: 14 },
      { id: "totalPaid", label: "Total Paid", sample: "Rp. 2.884.000", visible: layout.payment?.visible !== false, bold: true, align: "right", fontSize: layout.payment?.rows?.find((row) => row.key === "total_paid")?.size === "large" ? 16 : 12 },
    ],
  };
};

export const editorStateToPrintLayoutPayload = ({
  settings,
  companyName,
  companyAddress,
  footerText,
  fields,
  itemStyles,
}) => {
  const item = (id) => itemStyles?.find((value) => value.id === id);
  const row = (key, label, order, style) => ({
    key,
    label,
    order,
    visible: style?.visible !== false,
    bold: style?.bold !== false,
    align: style?.align || "left",
    size: Number(style?.fontSize) >= 15 ? "large" : "normal",
  });

  return {
    name: settings.name || `Booking V4 ${settings.print_type || "ticket"}`,
    print_type: settings.print_type || "ticket",
    company_id: settings.company_id || null,
    branch_id: settings.branch_id || null,
    printer_profile: settings.printer_profile || null,
    device_id: settings.device_id || null,
    paper_width: settings.paperWidth,
    printer_font: settings.printerFont,
    characters_per_line: settings.charsPerLine,
    line_height: settings.lineHeight,
    character_spacing: settings.characterSpacing,
    is_active: settings.is_active !== false,
    header: {
      company_name: { value: companyName, visible: true, bold: true, align: "center", size: "normal" },
      company_address: { value: companyAddress, visible: true, bold: false, align: "center", size: "normal" },
      logo_visible: true,
      logo_width: 150,
      spacing_before_logo: 0,
      spacing_after_logo: 0,
    },
    booking_details: (fields || []).map((field, index) => row(field.id, field.label, index + 1, field)),
    activity: {
      visible: item("itemHeader")?.visible !== false,
      title: "Activity",
      quantity_label: "Qty",
      show_price: true,
      item_visible: item("itemName")?.visible !== false,
      item_bold: item("itemName")?.bold === true,
      item_align: item("itemName")?.align || "left",
      quantity_visible: item("itemQty")?.visible !== false,
      quantity_bold: item("itemQty")?.bold === true,
      quantity_align: item("itemQty")?.align || "right",
      separator_before: true,
      separator_after: true,
    },
    meal: {
      visible: true,
      title: "Meals Voucher",
      item_label: "Item",
      quantity_label: "Qty",
      show_zero_quantity: true,
      show_note: true,
      item_bold: item("mealName")?.bold === true,
      quantity_bold: item("mealQty")?.bold === true,
      separator_before: true,
      separator_after: true,
    },
    payment: {
      visible: true,
      rows: ["total_guest", "payment_method", "subtotal", "tax", "total_paid"].map((key, index) =>
        row(key, key.replace(/_/g, " ").toUpperCase(), index + 1, key === "total_paid" ? item("totalPaid") : null)
      ),
      separator_before: true,
      separator_after: true,
    },
    signature: {
      visible: true,
      title: "Signature of Guest",
      title_visible: true,
      title_bold: true,
      guest_name_visible: true,
      guest_name_bold: false,
      align: "center",
      line_visible: true,
      space_before: 1,
      space_after: 1,
    },
    footer: {
      visible: true,
      text: footerText,
      bold: false,
      align: "center",
      size: "normal",
      separator_before: true,
      space_before: 1,
      space_after: 0,
      cut_after: true,
    },
  };
};
